// frontend/src/utils/logger.ts
/**
 * Production-safe logging utility
 * Automatically disabled in production, supports different log levels
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

interface LoggerConfig {
  level: LogLevel;
  prefix?: string;
  enableInProduction?: boolean;
}

class Logger {
  public config: LoggerConfig;
  private isProduction: boolean;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.isProduction = import.meta.env.PROD;
    this.config = {
      level: this.isProduction ? LogLevel.ERROR : LogLevel.DEBUG,
      prefix: '',
      enableInProduction: false,
      ...config,
    };
  }

  /**
   * Check if logging is enabled for the given level
   */
  private shouldLog(level: LogLevel): boolean {
    // In production, only log if explicitly enabled
    if (this.isProduction && !this.config.enableInProduction) {
      return level >= LogLevel.ERROR; // Only errors in production
    }
    return level >= this.config.level;
  }

  /**
   * Format log message with prefix and timestamp
   */
  private formatMessage(level: string, message: string, data?: any): any[] {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : '';
    const formattedMessage = `${timestamp} ${prefix} ${level}: ${message}`;

    if (data !== undefined) {
      return [formattedMessage, data];
    }
    return [formattedMessage];
  }

  /**
   * Debug level logging - only in development
   */
  debug(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(...this.formatMessage('DEBUG', message, data));
    }
  }

  /**
   * Info level logging
   */
  info(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(...this.formatMessage('INFO', message, data));
    }
  }

  /**
   * Warning level logging
   */
  warn(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(...this.formatMessage('WARN', message, data));
    }
  }

  /**
   * Error level logging - always enabled
   */
  error(message: string, error?: Error | any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorData = error instanceof Error 
        ? { message: error.message, stack: error.stack }
        : error;
      console.error(...this.formatMessage('ERROR', message, errorData));
    }
  }

  /**
   * Group logging for related messages
   */
  group(label: string, collapsed: boolean = false): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      if (collapsed) {
        console.groupCollapsed(label);
      } else {
        console.group(label);
      }
    }
  }

  /**
   * End group logging
   */
  groupEnd(): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.groupEnd();
    }
  }

  /**
   * Table logging for structured data
   */
  table(data: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.table(data);
    }
  }

  /**
   * Time tracking start
   */
  time(label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.time(label);
    }
  }

  /**
   * Time tracking end
   */
  timeEnd(label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.timeEnd(label);
    }
  }

  /**
   * Create a child logger with a specific prefix
   */
  child(prefix: string): Logger {
    return new Logger({
      ...this.config,
      prefix: this.config.prefix ? `${this.config.prefix}:${prefix}` : prefix,
    });
  }
}

// Create default logger instances
export const logger = new Logger();

// Create module-specific loggers
export const authLogger = new Logger({ prefix: 'Auth' });
export const apiLogger = new Logger({ prefix: 'API' });
export const dataLogger = new Logger({ prefix: 'DataManager' });
export const appLogger = new Logger({ prefix: 'App' });

// Helper function to configure all loggers
export function configureLogging(level: LogLevel, enableInProduction: boolean = false): void {
  const config = { level, enableInProduction };
  logger.config = { ...logger.config, ...config };
  authLogger.config = { ...authLogger.config, ...config };
  apiLogger.config = { ...apiLogger.config, ...config };
  dataLogger.config = { ...dataLogger.config, ...config };
  appLogger.config = { ...appLogger.config, ...config };
}

// Disable all logging (useful for tests)
export function disableLogging(): void {
  configureLogging(LogLevel.NONE);
}

// Enable debug logging (useful for development)
export function enableDebugLogging(): void {
  configureLogging(LogLevel.DEBUG, true);
}

export default logger;
